import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { Action, select, Store } from "@ngrx/store";
import { TrackingContext, VerificationAction } from "@obg/mat.ui.core";
import { CORE_ROUTE, CoreState, Jurisdiction, JurisdictionSelector, UiInteraction } from "@obg/ngx.core";
import {
	CacheFactory,
	CacheStore,
	DeviceConfigService,
	fromDaysToMilliseconds,
	isExternalUrl,
	LocationService,
	StorageMode,
} from "@obg/ngx.global";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { SUCCESSFUL_NOTIFICATION_SHOWN_KEY } from "./kyc-notification.const";
import { KycNotification, KycNotificationType } from "./kyc-notification.model";

@Component({
	selector: "obg-m-kyc-notification",
	templateUrl: "./kyc-notification.component.html",
	styleUrls: ["./kyc-notification.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class KycNotificationComponent implements OnChanges, OnInit, OnDestroy {
	@HostBinding("class.obg-m-kyc-notification") hostCssClass = true;
	@HostBinding("class.obg-m-kyc-notification-success") @coerceBoolean() success = false;
	@HostBinding("class.obg-m-kyc-notification-warning") @coerceBoolean() warning = false;
	@HostBinding("class.obg-m-kyc-notification-danger") @coerceBoolean() danger = false;

	@Input() notificationData: KycNotification | undefined;
	@Input() @coerceBoolean() hideVerifyButton = false;
	@Input() @coerceBoolean() plus18 = false;

	@Output() close = new EventEmitter<void>();

	verifyTrackingData: UiInteraction;
	jurisdiction: Jurisdiction | undefined;
	isDesktop!: boolean;

	private cache: CacheStore;
	private routeName: string | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private changeDetectorRef: ChangeDetectorRef,
		private cacheFactory: CacheFactory,
		private locationService: LocationService,
		private deviceConfigService: DeviceConfigService
	) {
		this.verifyTrackingData = {
			event: GtmEventAction.VerificationStartClick,
			context: TrackingContext.VerificationInteractionContext,
			data: { ItemClicked: VerificationAction.VerifyClick },
		};

		this.cache = this.cacheFactory.get(SUCCESSFUL_NOTIFICATION_SHOWN_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: fromDaysToMilliseconds(1),
		});
	}

	ngOnChanges(changes: { notificationData: SimpleChange }): void {
		if (changes.notificationData) {
			this.setNotificationType();
			this.toggleButtonVisibility();
		}
	}

	ngOnInit(): void {
		this.isDesktop = this.deviceConfigService.isDesktop();

		const routeName$ = this.store.pipe(RouteSelector.getName$).pipe(
			tap((routeName) => {
				this.routeName = routeName;
				this.toggleButtonVisibility();
			})
		);

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			take(1),
			tap((jurisdiction) => {
				this.jurisdiction = jurisdiction;
				this.verifyTrackingData.data = { ...this.verifyTrackingData.data, RegistrationType: jurisdiction };
			})
		);

		this.data$$ = merge(routeName$, jurisdiction$).subscribe(() => this.changeDetectorRef.markForCheck());
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	triggerActionOrNavigate(destination: string | Action): void {
		if (this.notificationData?.type === KycNotificationType.Success) {
			this.cache.setItem(SUCCESSFUL_NOTIFICATION_SHOWN_KEY, Date.now());
		}

		if (typeof destination === "string") {
			if (isExternalUrl(destination)) {
				this.locationService.navigate(destination);
				return;
			}

			destination = RouteAction.navigate(destination);
		}

		this.store.dispatch(destination);
	}

	private setNotificationType(): void {
		const notificationType = this.notificationData?.type;

		this.success = false;
		this.warning = false;
		this.danger = false;

		switch (notificationType) {
			case KycNotificationType.Success: {
				this.success = true;
				break;
			}
			case KycNotificationType.Warning: {
				this.warning = true;
				break;
			}
			case KycNotificationType.Danger: {
				this.danger = true;
				break;
			}
		}
	}

	private toggleButtonVisibility(): void {
		const isRouteNameRoot = this.routeName === CORE_ROUTE.verification.root;
		const isNotificationTypeWarning = this.notificationData?.type === KycNotificationType.Warning;

		if (isRouteNameRoot && isNotificationTypeWarning) {
			this.hideVerifyButton = true;

			return;
		}

		this.hideVerifyButton = false;
	}
}
